import { z } from 'zod';
import type { TaxRuleToken } from '@sleed/bizcommerce-api';
import { TaxStatus } from '@sleed/bizcommerce-api';

/**
 * Tax rule (Φορολογικός Κανόνας) edit form. Mirrors the CURRENT `TaxRuleToken` contract
 * (Core/server/Libraries/BizWeb.NC.Core/Models/Pricing/TaxRuleToken.cs): a geographic scope
 * (country / region / postal code) mapped to a tax status. NOTE: the legacy admin grid
 * (Core/client/admin/src/pages/Pricing/TaxRules/index.js) still binds the obsolete
 * tax_category_id / zip_code / rate fields, which no longer exist server-side — this page is
 * built against the live contract instead.
 *
 * country_id is required (a rule needs a geographic anchor, matching the legacy RequiredRule and
 * the entity's unique indexes, all keyed on a non-null CountryId). Region + postal code are
 * optional narrowing scopes; tax_status defaults to Normal.
 */
export const taxRuleSchema = z
	.object({
		id: z.number().optional(),
		country_id: z.number().nullable(),
		country_region_id: z.number().nullable(),
		postal_code: z.string().trim().max(20, 'validation.tooLong').nullable(),
		tax_status: z.nativeEnum(TaxStatus)
	})
	.superRefine((val, ctx) => {
		if (val.country_id === null) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['country_id'], message: 'validation.required' });
		}
	});

export type TaxRuleFormValues = z.infer<typeof taxRuleSchema>;

export const EMPTY_TAX_RULE: TaxRuleFormValues = {
	country_id: null,
	country_region_id: null,
	postal_code: null,
	tax_status: TaxStatus.Normal
};

export function tokenToForm(token: TaxRuleToken): TaxRuleFormValues {
	return {
		id: token.id,
		country_id: token.country_id ?? null,
		country_region_id: token.country_region_id ?? null,
		postal_code: token.postal_code ?? null,
		tax_status: token.tax_status ?? TaxStatus.Normal
	};
}

export function formToToken(form: TaxRuleFormValues): TaxRuleToken {
	return {
		id: form.id,
		country_id: form.country_id,
		country_region_id: form.country_region_id,
		postal_code: form.postal_code?.trim() || null,
		tax_status: form.tax_status
	};
}
